// A spotlight tour for the قرعه‌کشی landing.
//
// Hand-rolled rather than a library: the app carries no tour dependency, and
// adding one for a single marketing page means re-resolving the whole
// react-scripts 5 tree for ~200 lines of behaviour we can read.
//
// Three things it has to get right, all of which cost a round of debugging:
//
//   * **It renders through a portal into `<body>`.** `position: fixed` is not
//     fixed to the viewport inside an ancestor with a `transform`, and
//     `FundLayout` wraps every route in a framer-motion `x` animation. An
//     overlay mounted in place would pin itself to that box and drift with it.
//   * **It re-measures on scroll.** The spotlight is a hole punched at the
//     target's `getBoundingClientRect()`, which is viewport-relative and stale
//     the instant anything moves.
//   * **It never floats the card next to the hole.** See `cardStyle` below —
//     a tall anchor leaves room on neither side and the card ends up laid out
//     off the top of the window.

import { useCallback, useEffect, useLayoutEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { motion } from 'framer-motion'

export interface TourStep {
    /** stable id, reported to analytics — do not renumber these */
    key: string
    title: string
    body: string
    /**
     * CSS selector for the element to spotlight.
     *
     * A selector rather than a plain `data-tour` value so a step can reach
     * *inside* a section it does not own — `[data-tour="summary"] .rounded-card`
     * picks out the balance card without the product component having to carry
     * a marketing attribute. A selector that matches nothing is not an error:
     * the step falls back to a centred card over a plain dim, which is the
     * right outcome when a highlight target has been renamed out from under us.
     *
     * Omit it entirely for a step that speaks about the page as a whole.
     */
    anchor?: string
}

interface Props {
    steps: ReadonlyArray<TourStep>
    open: boolean
    /** fired on entering each step, including the first */
    onStep?: (index: number, step: TourStep) => void
    /** the reader gave up — `index` is where they were */
    onSkip?: (index: number, step: TourStep) => void
    /**
     * What the two buttons say.
     *
     * The tour used to be one fixed four-step run, so «بعدی» and «فهمیدم» were
     * the only words it ever needed. Now it is opened one topic at a time from
     * a list, and the honest labels there are «موضوع بعدی» and «فهرست
     * موضوع‌ها» — a card that says «فهمیدم» after two sentences is claiming
     * the reader finished something they only glanced at.
     */
    primaryLabel?: string
    secondaryLabel?: string
    /** the reader reached the end and pressed the final button */
    onDone?: () => void
}

interface Rect {
    top: number
    left: number
    width: number
    height: number
}

/** Breathing room around the highlighted element, in px. */
const PADDING = 6
/** Above this fraction of the viewport, an anchor counts as "tall". */
const TALL = 0.5
/** Where a tall anchor's top is parked, as a fraction of the viewport. */
const TALL_TOP = 0.32
const CARD_MAX_WIDTH = 320
const EDGE = 12
/**
 * How far down a top-pinned card starts.
 *
 * The «پشتیبانی آنلاین» and «همیان چیست» launchers are fixed at the top-left
 * corner, and a card pinned to `EDGE` lands on top of them — the reader is
 * reading an explanation through two buttons they can no longer press. This is
 * the height of that stack plus its own margin.
 */
const TOP_CLEARANCE = 128

const sameRect = (a: Rect | null, b: Rect | null) =>
    a === b ||
    (!!a &&
        !!b &&
        Math.round(a.top) === Math.round(b.top) &&
        Math.round(a.left) === Math.round(b.left) &&
        Math.round(a.width) === Math.round(b.width) &&
        Math.round(a.height) === Math.round(b.height))

const measure = (anchor?: string): Rect | null => {
    if (!anchor) return null
    const el = document.querySelector<HTMLElement>(anchor)
    if (!el) return null
    const box = el.getBoundingClientRect()
    if (box.width === 0 && box.height === 0) return null

    // Clamped to the viewport, because the padding can push the hole off the
    // edge for an element that is already flush with it — the call-to-action
    // bar is pinned to `bottom: 0`, so its highlight ring would be drawn 6px
    // below the bottom of the screen and lose its lower corners. Clamping also
    // means a partly-scrolled anchor gets a spotlight over the part you can
    // actually see, rather than one hanging off into nowhere.
    const top = Math.max(0, box.top - PADDING)
    const left = Math.max(0, box.left - PADDING)
    const bottom = Math.min(window.innerHeight, box.bottom + PADDING)
    const right = Math.min(window.innerWidth, box.right + PADDING)
    if (bottom <= top || right <= left) return null

    return { top, left, width: right - left, height: bottom - top }
}

export default function LandingTour({
    steps,
    open,
    onStep,
    onSkip,
    onDone,
    primaryLabel,
    secondaryLabel,
}: Props) {
    const [index, setIndex] = useState(0)
    const [rect, setRect] = useState<Rect | null>(null)

    const step = steps[index]
    const isLast = index === steps.length - 1

    // Rewind on CLOSE, not on open, so «راهنما» always restarts from the top.
    //
    // Resetting on open looks equivalent and is not: the reset lands one render
    // *after* the tour is already open, so the step-reporting effect below
    // fires once for whatever step the reader quit on and again for step 0 —
    // a phantom step in the funnel every time the tour is reopened.
    useEffect(() => {
        if (!open) setIndex(0)
    }, [open])

    const sync = useCallback(() => {
        setRect((current) => {
            const next = measure(step?.anchor)
            return sameRect(current, next) ? current : next
        })
    }, [step?.anchor])

    // Bring the step into view, then keep the hole glued to it. `useLayoutEffect`
    // so the first measurement happens before the overlay paints — measuring in
    // a plain effect shows one frame of a spotlight at 0,0.
    useLayoutEffect(() => {
        if (!open || !step) return

        if (step.anchor) {
            // Instant, not smooth. A smooth scroll is driven by
            // requestAnimationFrame, so it is throttled to a standstill in a
            // backgrounded webview and otherwise animates *under* a spotlight
            // measured before it started — leaving the hole somewhere the
            // reader is not looking, or off the screen entirely. Jumping puts
            // the element where `sync()` is about to measure it, this frame.
            const el = document.querySelector<HTMLElement>(step.anchor)
            if (el) {
                const box = el.getBoundingClientRect()
                if (box.height > window.innerHeight * TALL) {
                    // A section taller than the screen cannot be centred: the
                    // browser would put its middle at the middle, pushing the
                    // top off, and the clamped hole would then cover the whole
                    // viewport with the card stranded on top of it. Park its
                    // top a third of the way down instead, so the hole runs
                    // from there to the bottom edge and the card has the strip
                    // above it to sit in.
                    window.scrollBy({
                        top: box.top - window.innerHeight * TALL_TOP,
                        behavior: 'auto',
                    })
                } else {
                    el.scrollIntoView({ behavior: 'auto', block: 'center' })
                }
            }
        }
        sync()

        // Late re-measures for whatever settles after the jump: an image
        // resizing above the anchor, a sticky bar collapsing, the messenger's
        // own chrome appearing.
        const timers = [
            window.setTimeout(sync, 120),
            window.setTimeout(sync, 420),
        ]
        window.addEventListener('scroll', sync, true)
        window.addEventListener('resize', sync)
        return () => {
            timers.forEach(window.clearTimeout)
            window.removeEventListener('scroll', sync, true)
            window.removeEventListener('resize', sync)
        }
    }, [open, step, sync])

    useEffect(() => {
        if (open && step) onStep?.(index, step)
        // Reporting is keyed on the step, not on every render of it.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, index])

    if (!open || !step) return null

    const viewportH = window.innerHeight

    // The card is pinned to whichever edge the spotlight is furthest from —
    // never floated next to it.
    //
    // Floating it looks tidier on paper and does not survive contact: a step
    // that highlights a tall section (the صندوق summary is 620px of an 812px
    // screen) leaves room neither above nor below, and the card gets laid out
    // off the top of the window where nobody can read or dismiss it. Pinning
    // needs no knowledge of the card's own height, so it cannot reach that
    // state at all.
    const spotlightMiddle = rect ? rect.top + rect.height / 2 : viewportH / 2
    const cardStyle: React.CSSProperties = {
        left: EDGE,
        right: EDGE,
        marginInline: 'auto',
        ...(spotlightMiddle > viewportH / 2
            ? { top: TOP_CLEARANCE }
            : { bottom: EDGE }),
    }

    const finish = () => {
        if (isLast) onDone?.()
        else setIndex((i) => i + 1)
    }

    return createPortal(
        <motion.div
            key="landing-tour"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.18 }}
            className="fixed inset-0 z-[1000]"
            role="dialog"
            aria-modal="true"
            aria-label="راهنمای صندوق قرعه‌کشی"
        >
            {/* The dimmer. When a step has an anchor it is the ring around the
                hole (an outsized box-shadow); otherwise a plain sheet. */}
            {rect ? (
                <div
                    className="absolute rounded-[12px] pointer-events-none"
                    style={{
                        top: rect.top,
                        left: rect.left,
                        width: rect.width,
                        height: rect.height,
                        boxShadow:
                            '0 0 0 9999px rgba(0,0,0,0.62), 0 0 0 2px rgba(255,255,255,0.9) inset',
                    }}
                />
            ) : (
                <div className="absolute inset-0 bg-black/60" />
            )}

            {/* Swallows taps on the page underneath: while the tour is up, the
                only controls are its own. */}
            <div className="absolute inset-0" onClick={() => finish()} />

            <motion.div
                key={step.key}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2 }}
                className="absolute rounded-card bg-white p-3.5 shadow-lg"
                style={{ ...cardStyle, maxWidth: CARD_MAX_WIDTH }}
                onClick={(event) => event.stopPropagation()}
            >
                <p className="text-base font-bold text-black">{step.title}</p>
                <p className="text-sm text-text-secondary leading-6 mt-2">
                    {step.body}
                </p>

                <div className="flex items-center justify-between mt-4">
                    {steps.length > 1 && (
                        <div className="flex items-center gap-1.5">
                            {steps.map((s, i) => (
                                <span
                                    key={s.key}
                                    className={`h-1.5 rounded-full transition-all ${
                                        i === index
                                            ? 'w-4 bg-green-1'
                                            : 'w-1.5 bg-divider'
                                    }`}
                                />
                            ))}
                        </div>
                    )}

                    <div className="flex items-center gap-2">
                        {/* `!isLast` alone was right only while this was one
                            fixed run, where «رد کردن» on the final card meant
                            nothing that «فهمیدم» did not already do. A caller
                            that names the second button means it on every card
                            — a topic tour is a single step, and hiding
                            «فهرست موضوع‌ها» there strands the reader in the one
                            explanation they opened. */}
                        {(secondaryLabel || !isLast) && (
                            <button
                                type="button"
                                className="h-9 px-3 text-sm text-text-secondary"
                                onClick={() => onSkip?.(index, step)}
                            >
                                {secondaryLabel ?? 'رد کردن'}
                            </button>
                        )}
                        <button
                            type="button"
                            className="h-9 px-4 rounded-[10px] bg-green-1 text-white text-sm font-bold active:scale-[0.99]"
                            onClick={finish}
                        >
                            {primaryLabel ?? (isLast ? 'فهمیدم' : 'بعدی')}
                        </button>
                    </div>
                </div>
            </motion.div>
        </motion.div>,
        document.body
    )
}
